import { useCallback, useEffect, useRef, useState } from "react";
import { useSheetSignature as useSheetSignatureBase } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import { readCache } from "../local/db";
import { cachedSignatureView, imageUrlToDataUrl, withInlinedImages } from "../util/signatureImages";

const signatureCacheKey = (sheetId) => `signature:${sheetId}`;

// scribe wrapper around common-fe's useSheetSignature (card #735): wires
// score-api's getScoreSheetSignature as the fetcher (mirrors
// scoreadmin-fe/src/hooks/useSheetSignature.js). Pass `enabled` (the sheet's
// signed state, e.g. `Boolean(isCompleted)`) so the fetch only happens once a
// signature actually exists to show.
//
// card #3196 (docs/score/scribe-resilience.md I8) - cache-first: the last
// signature read for this sheet paints from IndexedDB first, the network (only
// while online) refines it, and a failed or skipped read keeps what is painted.
// Offline with nothing cached the box is simply empty and NO request is made.
export function useSheetSignature(sheetId, opts) {
  const scoreApi = useScoreApi();
  const { online } = useScoreContext();
  const [cached, setCached] = useState(null);
  const enabled = opts?.enabled ?? true;
  // ScoringForm is not remounted between /rides/:rideId/score routes, so this
  // hook sees sheetId change in place: a late answer for the PREVIOUS sheet must
  // never land as the new sheet's signature.
  const currentSheetId = useRef(sheetId);
  currentSheetId.current = sheetId;
  // Which sheet the shared base hook's current answer belongs to. The base hook
  // keeps its last answer until the next fetch lands, so on an in-place sheetId
  // change it would otherwise hand sheet A's signature to sheet B for a render.
  const freshFor = useRef({ id: null, fresh: null });

  useEffect(() => {
    // Drop the previous sheet's cached record before reading this one's, else a
    // sheet with no cached row would paint the last sheet's judge signature.
    setCached(null);
    if (!sheetId || !enabled) {
      return undefined;
    }
    let cancelled = false;
    readCache
      .get(signatureCacheKey(sheetId))
      .then((row) => {
        if (!cancelled && row) setCached(row);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sheetId, enabled]);

  const fetchSignature = useCallback(
    async (id) => {
      const fresh = unwrap(await scoreApi.scoreSheets.getScoreSheetSignature(id));
      freshFor.current = { id, fresh };
      if (!fresh) return fresh;
      let prior;
      try {
        prior = await readCache.get(signatureCacheKey(id));
      } catch {
        prior = undefined;
      }
      // Keep the image bytes beside the record: the presigned URL expires, so
      // offline only the data URL can paint (best effort, see signatureImages).
      const withBytes = await withInlinedImages(fresh, prior, imageUrlToDataUrl);
      readCache.put(signatureCacheKey(id), withBytes).catch(() => {});
      if (currentSheetId.current === id) setCached(withBytes);
      return fresh;
    },
    [scoreApi],
  );
  const base = useSheetSignatureBase(sheetId, fetchSignature, {
    ...opts,
    enabled: enabled && online,
  });
  const baseSignature =
    base.signature && freshFor.current.fresh === base.signature && freshFor.current.id === sheetId
      ? base.signature
      : null;
  const signature = baseSignature ?? cachedSignatureView(cached);
  return { ...base, signature, loading: base.loading && !signature };
}

export default useSheetSignature;
